import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as localService from "../../services/localService";
import { getDeviceId } from "../../services/localService";
import messagingService from "../../services/host-apiServices/messagingService";
import { getMessaging, getToken } from "firebase/messaging";
import { getApp } from "firebase/app";
import logger from "../../utils/logger";
import apiV2Service from "../../services/apiV2Service";
import { v2PublicApiService } from "../../services/apiV2Service";
import billingService from "../../services/billingService";
import { RoleConstants } from "../../utils/constants";
import {
  setCurrencies,
  setCountries,
  setServiceCategories,
  setServiceSubcategories,
} from "../../redux/actions/dashboard";
import {
  setSubscriptions,
  setActiveSubscription,
  setSubscriptionWallChecked,
  setFeatureFlags,
  setFeatureFlagsLoading,
  setFeatureFlagsError,
} from "../../redux/actions/subscription";
import { fetchPaymentSetups } from "../../redux/actions/paymentSetup";
import { fetchEnabledFeatures } from "../../utils/subscriptionAccessControl";
import { identifyUser } from "../../utils/analytics";

// Module-level variable for token refresh interval
let tokenRefreshInterval: NodeJS.Timeout | null = null;

interface AuthResponse {
  user: any;
  access_token: string;
  refresh_token: string;
}

interface PostAuthFlowOptions {
  shouldRegisterFCM?: boolean;
  customNavigation?: (role: string) => void;
}

/**
 * Shared post-authentication flow that handles:
 * 1. Saving user data and tokens
 * 2. Setting currency and company ID if available
 * 3. Loading currencies and countries into Redux (global cache)
 * 4. Registering FCM token for push notifications
 * 5. Setting up token refresh
 * 6. Setting up session monitoring
 * 7. Navigation based on user role
 */
export const usePostAuthFlow = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  /**
   * Load currencies and store in Redux
   */
  const loadCurrencies = async (): Promise<void> => {
    try {
      logger.log("🪙 Loading currencies after login...");
      logger.log("🪙 Loading currencies after login...");

      const response = await billingService.getCurrencies({ limit: 500 });

      if (response && response.data && response.data.data) {
        dispatch(setCurrencies(response.data.data));
        logger.log(`✅ Loaded ${response.data.data.length} currencies`);
        logger.log(`✅ Loaded ${response.data.data.length} currencies`);
      }
    } catch (error) {
      logger.error("❌ Error loading currencies:", error);
      logger.error("❌ Error loading currencies:", error);
    }
  };

  /**
   * Load countries and store in Redux
   */
  const loadCountries = async (): Promise<void> => {
    try {
      logger.log("🌍 Loading countries after login...");
      logger.log("🌍 Loading countries after login...");

      const response = await v2PublicApiService.getCountries();

      if (response && response.data && response.data.data) {
        dispatch(setCountries(response.data.data));
        logger.log(`✅ Loaded ${response.data.data.length} countries`);
        logger.log(`✅ Loaded ${response.data.data.length} countries`);
        logger.log("gjfkkrjejrjr [LOGIN] Countries response sample:", response.data.data.slice(0, 5));
        logger.log("gjfkkrjejrjr [LOGIN] Countries response sample:", response.data.data.slice(0, 5));

        // Find Kenya (ID 119) specifically
        const kenya = response.data.data.find((c: any) => c.id === 119);
        logger.log("gjfkkrjejrjr [LOGIN] Kenya country data (ID 119):", kenya);
        logger.log("gjfkkrjejrjr [LOGIN] Kenya country data (ID 119):", kenya);

        // Find Ghana (ID 84) specifically
        const ghana = response.data.data.find((c: any) => c.id === 84);
        logger.log("gjfkkrjejrjr [LOGIN] Ghana country data (ID 84):", ghana);
        logger.log("gjfkkrjejrjr [LOGIN] Ghana country data (ID 84):", ghana);
      }
    } catch (error) {
      logger.error("❌ Error loading countries:", error);
      logger.error("❌ Error loading countries:", error);
    }
  };

  /**
   * Load service categories and subcategories, store in Redux
   */
  const loadServiceCategories = async (): Promise<void> => {
    try {
      logger.log("🏷️ Loading service categories after login...");
      const response = await billingService.getAllServiceCategories({ type: "END_USER" });

      if (response?.data?.data) {
        const categories = response.data.data;
        logger.log(`✅ Loaded ${categories.length} service categories`);

        const flatCategories = categories.map(({ subcategories, ...cat }: any) => cat);
        dispatch(setServiceCategories(flatCategories));

        const allSubcategories = categories.flatMap((cat: any) =>
          (cat.subcategories || []).map((sub: any) => ({
            ...sub,
            categoryId: cat.id,
            categoryName: cat.name,
            categoryCode: cat.code,
          }))
        );
        dispatch(setServiceSubcategories(allSubcategories));
        logger.log(`✅ Loaded ${allSubcategories.length} subcategories across ${categories.length} categories`);
      } else {
        dispatch(setServiceCategories([]));
        dispatch(setServiceSubcategories([]));
      }
    } catch (error) {
      logger.error("❌ Error loading service categories:", error);
      dispatch(setServiceCategories([]));
      dispatch(setServiceSubcategories([]));
    }
  };

  /**
   * Load subscription feature flags and store in Redux
   * Uses active recurring subscription already fetched to avoid duplicate API calls
   * @param {Object|null} activeRecurringSubscription - Already fetched active recurring subscription
   */
  const loadSubscriptionFeatureFlags = async (activeRecurringSubscription: any = null): Promise<void> => {
    try {
      logger.log("🔐 Loading subscription feature flags after login...");
      logger.log("🔐 Loading subscription feature flags after login...");

      dispatch(setFeatureFlagsLoading(true));

      if (!activeRecurringSubscription) {
        logger.log("⚠️ No active recurring subscription found, all features disabled");
        logger.log("⚠️ No active recurring subscription found, all features disabled");
        logger.log("⚠️ This means no API calls to /api/v1/packages/{id}/applicable will be made");
        dispatch(
          setFeatureFlags({
            BOOKING_BY_CUSTOMER: false,
            CUSTOMER_MANAGEMENT: false,
            CALENDAR_VIEW_OF_BOOKINGS: false,
            TEAM_MESSAGING: false,
            CUSTOMER_MESSAGING: false,
            BOOKING_FOR_CUSTOMER: false,
            MULTI_LOCATION: false,
            WHITE_LABELING: false,
          })
        );
        dispatch(setFeatureFlagsLoading(false));
        return;
      }

      logger.log("Active recurring subscription found, fetching feature flags:", activeRecurringSubscription);
      logger.log("Active recurring subscription found, fetching feature flags:", activeRecurringSubscription);

      // Use the active subscription to fetch feature flags (no duplicate API call)
      // This will: 1. Get package ID, 2. Call /api/v1/packages/{id}/applicable, 3. Extract subcategories from offers, 4. Save to Redux
      const featureFlags = await fetchEnabledFeatures(true, activeRecurringSubscription, dispatch); // Force refresh on login, pass dispatch to save to Redux

      // Convert Set to object format for Redux
      const featureFlagsObject = {
        BOOKING_BY_CUSTOMER: featureFlags.has("BOOKING_BY_CUSTOMER"),
        CUSTOMER_MANAGEMENT: featureFlags.has("CUSTOMER_MANAGEMENT"),
        CALENDAR_VIEW_OF_BOOKINGS: featureFlags.has("CALENDAR_VIEW_OF_BOOKINGS"),
        TEAM_MESSAGING: featureFlags.has("TEAM_MESSAGING"),
        CUSTOMER_MESSAGING: featureFlags.has("CUSTOMER_MESSAGING"),
        BOOKING_FOR_CUSTOMER: featureFlags.has("BOOKING_FOR_CUSTOMER"),
        MULTI_LOCATION: featureFlags.has("MULTI_LOCATION"),
        WHITE_LABELING: featureFlags.has("WHITE_LABELING"),
      };

      dispatch(setFeatureFlags(featureFlagsObject));
      dispatch(setFeatureFlagsLoading(false));

      logger.log(`✅ Loaded subscription feature flags:`, featureFlagsObject);
      logger.log(`✅ Loaded subscription feature flags:`, featureFlagsObject);
    } catch (error) {
      logger.error("❌ Error loading subscription feature flags:", error);
      logger.error("❌ Error loading subscription feature flags:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to load feature flags";
      dispatch(setFeatureFlagsError(errorMessage));
      dispatch(setFeatureFlagsLoading(false));
    }
  };

  /**
   * Check if organization has any active, non-expired subscriptions and dispatch to Redux
   * @returns {Promise<{hasActive: boolean, subscriptions: any[], activeRecurringSubscription: any}>} Subscription check result
   */
  const checkOrganizationSubscriptions = async (
    organizationId: number
  ): Promise<{ hasActive: boolean; subscriptions: any[]; activeRecurringSubscription: any }> => {
    try {
      logger.log("🔍 Checking organization subscriptions for ID:", organizationId);
      logger.log("🔍 Checking organization subscriptions for ID:", organizationId);

      const response = await billingService.getOrganizationSubscriptions({
        organizationId,
        orderBy: "subscriptionDate:desc", // Get most recent subscriptions first
        limit: 50, // Get up to 50 subscriptions to check
        status: "USABLE",
      });

      logger.log("📡 Billing API response:", response);
      logger.log("📡 Billing API response:", response);

      if (!response?.data?.data || !Array.isArray(response.data.data)) {
        logger.log("No subscriptions found or invalid response format");
        // Dispatch empty subscriptions to Redux
        dispatch(setSubscriptions([]));
        dispatch(setActiveSubscription(null));
        return { hasActive: false, subscriptions: [], activeRecurringSubscription: null };
      }

      const subscriptions = response.data.data;
      logger.log(`Found ${subscriptions.length} subscriptions`);

      // Dispatch all subscriptions to Redux
      dispatch(setSubscriptions(subscriptions));

      // Check for any active subscription
      // A subscription is considered active if:
      // 1. status is "ACTIVE" or "USABLE" (PENDING means not paid yet, so NOT active)
      // 2. Either no nextRenewalDate (null = no expiry) OR nextRenewalDate is in the future
      const now = new Date();
      let activeRecurringSubscription: any = null;

      subscriptions.forEach((subscription: any) => {
        const nextRenewalDate = subscription.nextRenewalDate ? new Date(subscription.nextRenewalDate) : null;
        const status = subscription.status;
        const subscriptionRecurring = subscription.recurring;
        const packageRecurring = subscription.servicePackage?.recurring;

        const isActiveStatus =
          status === "ACTIVE" || status === "USABLE" || status === "CANCELLED" || status === "CANCELED";
        const isActive = isActiveStatus && (nextRenewalDate === null || nextRenewalDate > now);
        const isRecurring = subscriptionRecurring !== false && packageRecurring !== false;

        logger.log(
          `Subscription ${subscription.id}: status=${status}, isActive=${isActive}, isRecurring=${isRecurring}, nextRenewal=${nextRenewalDate}`
        );

        if (!activeRecurringSubscription && isActive && isRecurring) {
          activeRecurringSubscription = subscription;
        }
      });

      const hasActiveSubscription = !!activeRecurringSubscription;

      // Store the active subscription in localStorage and Redux
      if (activeRecurringSubscription) {
        logger.log("💾 Storing active subscription:", activeRecurringSubscription);
        logger.log("💾 Storing active subscription:", activeRecurringSubscription);
        localService.saveActiveSubscription(activeRecurringSubscription);
        dispatch(setActiveSubscription(activeRecurringSubscription));
      } else {
        logger.log("🗑️ No active subscription found, clearing any stored subscription");
        logger.log("🗑️ No active subscription found, clearing any stored subscription");
        localService.clearActiveSubscription();
        dispatch(setActiveSubscription(null));
      }

      logger.log("Has active subscription:", hasActiveSubscription);
      logger.log("Has active recurring subscription:", !!activeRecurringSubscription);

      return {
        hasActive: hasActiveSubscription,
        subscriptions,
        activeRecurringSubscription: activeRecurringSubscription || null,
      };
    } catch (error) {
      logger.error("Error checking organization subscriptions:", error);
      // Dispatch error state to Redux
      dispatch(setSubscriptions([]));
      dispatch(setActiveSubscription(null));
      // If there's an error, assume no subscription to be safe
      return { hasActive: false, subscriptions: [], activeRecurringSubscription: null };
    }
  };

  const getFCMToken = async (): Promise<string | null> => {
    try {
      logger.log("Initializing Firebase messaging...");
      const app = getApp();
      const messaging = getMessaging(app);

      // Request notification permission first
      logger.log("Requesting notification permission...");
      const permission = await Notification.requestPermission();
      logger.log("Notification permission status:", permission);

      if (permission === "granted") {
        logger.log("Getting FCM token...");
        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        });
        logger.log("FCM Token obtained:", token);
        return token;
      } else {
        logger.log("Notification permission denied");
        return null;
      }
    } catch (error) {
      logger.error("Error getting FCM token:", error);
      return null;
    }
  };

  const registerFCMToken = async (): Promise<void> => {
    try {
      const fcmToken = await getFCMToken();
      if (fcmToken) {
        await messagingService.registerFCMToken({
          deviceId: await getDeviceId(),
          platform: "web",
          token: fcmToken,
        });
        logger.log("FCM token registered successfully");
      }
    } catch (error) {
      logger.error("Error registering FCM token:", error);
    }
  };

  /**
   * Set up proactive token refresh every 1 minute
   */
  const setupTokenRefresh = (): void => {
    logger.log("Setting up token refresh interval...");

    // Clear any existing interval
    if (tokenRefreshInterval) {
      logger.log("Clearing existing token refresh interval");
      clearInterval(tokenRefreshInterval);
      tokenRefreshInterval = null;
    }

    logger.log("Creating setInterval with 25 minute delay...");
    const intervalId = setInterval(
      async () => {
        try {
          logger.log("🎯 TOKEN REFRESH INTERVAL TRIGGERED!");

          const refreshToken = localService.getRefreshToken();
          if (!refreshToken) {
            logger.warn("No refresh token available for proactive refresh");
            return;
          }

          const shouldRefresh = localService.shouldRefreshToken();
          if (shouldRefresh) {
            logger.log("Proactively refreshing token...");
            const response = await apiV2Service.refreshToken(refreshToken);

            localStorage.setItem("access_token", response.access_token);
            localStorage.setItem("refresh_token", response.refresh_token);

            logger.log("Token refreshed successfully");
          } else {
            logger.log("Token does not need refresh yet");
          }

          // Also check for session expiry during token refresh cycle
          const wasLoggedOut = localService.checkTokenExpiryAndLogout();
          if (wasLoggedOut) {
            logger.log("User was automatically logged out during token refresh cycle");
            return;
          }
        } catch (error) {
          logger.error("Error during proactive token refresh:", error);
          // If refresh fails, clear the interval and redirect to login
          if (tokenRefreshInterval) {
            clearInterval(tokenRefreshInterval);
            tokenRefreshInterval = null;
          }
          localService.clearAllUserData();
          window.location.href = "/signin";
        }
      },
      25 * 60 * 1000
    ); // 25 minutes

    // Store the interval ID
    tokenRefreshInterval = intervalId;
    logger.log("✅ Interval created with ID:", intervalId);
    logger.log("✅ tokenRefreshInterval variable set to:", tokenRefreshInterval);
    logger.log("✅ Token refresh interval set up for every 25 minutes, interval ID:", tokenRefreshInterval);

    // Debug: Store interval ID globally for debugging
    (window as any).debugTokenRefreshInterval = tokenRefreshInterval;

    // Add global function to manually trigger token refresh for testing
    (window as any).triggerTokenRefresh = async () => {
      logger.log("🔧 MANUAL TOKEN REFRESH TRIGGERED!");
      logger.log("🔧 MANUAL TOKEN REFRESH TRIGGERED!");

      const refreshToken = localService.getRefreshToken();
      if (!refreshToken) {
        logger.warn("No refresh token available for manual refresh");
        logger.warn("No refresh token available for manual refresh");
        return;
      }

      try {
        const response = await apiV2Service.refreshToken(refreshToken);
        localStorage.setItem("access_token", response.access_token);
        localStorage.setItem("refresh_token", response.refresh_token);
        logger.log("Manual token refresh successful");
        logger.log("Manual token refresh successful");
      } catch (error) {
        logger.error("Manual token refresh failed:", error);
        logger.error("Manual token refresh failed:", error);
      }
    };

    // Add global function to test session expiry
    (window as any).testSessionExpiry = () => {
      logger.log("🧪 TESTING SESSION EXPIRY!");
      logger.log("🧪 TESTING SESSION EXPIRY!");
      localService.testSessionExpiry();
    };
  };

  const defaultNavigation = async (role: string, user: any): Promise<void> => {
    // Check if user has subscription for specific org types
    const orgTypeCode = user?.organization?.type?.code;
    const organizationId = user?.organizationId;
    const roleCode = user?.organizationRole?.code;

    logger.log("🧭 Navigation - Role:", role, "OrgTypeCode:", orgTypeCode, "User:", user);
    logger.log("🧭 Navigation - Role:", role, "OrgTypeCode:", orgTypeCode, "User:", user);

    // HOST_PROPERTY_MANAGEMENT and CORPORATE_CUSTOMER require subscription
    // ROLE_HOST_CUSTOMER_INDIVIDUAL does not require subscription even under these org types
    const shouldCheckSubscription =
      (orgTypeCode === "HOST_PROPERTY_MANAGEMENT" || orgTypeCode === "CORPORATE_CUSTOMER") &&
      roleCode !== "ROLE_HOST_CUSTOMER_INDIVIDUAL";

    if (shouldCheckSubscription) {
      logger.log(
        "🎯 Organization requiring subscription detected, orgTypeCode:",
        orgTypeCode,
        "roleCode:",
        roleCode,
        "organizationId:",
        organizationId
      );
      logger.log(
        "🎯 Organization requiring subscription detected, orgTypeCode:",
        orgTypeCode,
        "roleCode:",
        roleCode,
        "organizationId:",
        organizationId
      );

      if (organizationId) {
        // Check for active subscriptions using API call
        logger.log("🚀 About to check organization subscriptions...");
        logger.log("🚀 About to check organization subscriptions...");
        const result = await checkOrganizationSubscriptions(organizationId);
        const hasActiveSubscription = result.hasActive;

        logger.log("✅ Subscription check completed:", { hasActiveSubscription, organizationId, orgTypeCode });
        logger.log("✅ Subscription check completed:", { hasActiveSubscription, organizationId, orgTypeCode });

        // Mark subscription wall as checked
        dispatch(setSubscriptionWallChecked(true));

        if (hasActiveSubscription) {
          // User has active subscription, navigate to role-specific path
          logger.log("✅ User has active subscription, navigating to role-specific path");
          logger.log("✅ User has active subscription, navigating to role-specific path");

          if (
            (orgTypeCode === "HOST_PROPERTY_MANAGEMENT" || orgTypeCode === "HOST_PROPERTY_DISTRIBUTION") &&
            roleCode !== "ROLE_HOST_CUSTOMER_INDIVIDUAL"
          ) {
            try {
              logger.log("🔍 Checking for existing payout account for HOST_PROPERTY_MANAGEMENT...");
              const accountResponse = await billingService.getOrganizationAccount(organizationId);

              // If successful response but no account data, or 404 (handled in catch), redirect
              if (!accountResponse?.data?.data) {
                logger.log("⚠️ No payout account found, redirecting to /host/payment-setup");
                history.push("/host/payment-setup");
                return;
              }
              logger.log("✅ Payout account found, proceeding to dashboard");
            } catch (error: any) {
              // If error is 404, it means account doesn't exist
              if (error.response?.status === 404) {
                logger.log("⚠️ No payout account found (404), redirecting to /host/payment-setup");
                history.push("/host/payment-setup");
                return;
              }
              // Other errors, log and proceed (don't block login if API fails?)
              // Or block? "block their access" says block. Let's block on error for safety or assume 404.
              // For now, if API fails, maybe let them through or showing error is better.
              // Safer to redirect if we are STRICT about blocking.
              logger.error("❌ Error checking payout account, redirecting to setup just in case:", error);
              history.push("/host/payment-setup");
              return;
            }
          }

          // If CORPORATE_CUSTOMER has subscription, they go to /startbooking (or default role nav)
          if (orgTypeCode === "CORPORATE_CUSTOMER") {
            logger.log("User has active subscription, redirecting to /startbooking");
            history.push("/startbooking");
            return;
          }

          const navigationPath = RoleConstants.getNavigationPath(role);
          logger.log("📍 Navigating to:", navigationPath);
          history.push(navigationPath);
          return;
        } else {
          // User has no active subscription, redirect to pricing
          // CORPORATE_CUSTOMER should go to subscriber pricing, not host pricing
          const redirectPath = orgTypeCode === "CORPORATE_CUSTOMER" ? "/subscriber/pricing" : "/host/pricing";
          logger.log("❌ User has NO active subscription, redirecting to", redirectPath);
          logger.log("❌ User has NO active subscription, redirecting to", redirectPath);
          history.push(redirectPath);
          return;
        }
      } else {
        // Fallback if no organizationId but type requires check - treat as no subscription
        const redirectPath = orgTypeCode === "CORPORATE_CUSTOMER" ? "/subscriber/pricing" : "/host/pricing";
        logger.log("No organization ID found for required type, redirecting to", redirectPath);
        dispatch(setSubscriptionWallChecked(true));
        history.push(redirectPath);
        return;
      }
    }

    // For all other org types (HOST_PROPERTY_DISTRIBUTION, etc.), no subscription check needed
    logger.log("No subscription check needed for org type:", orgTypeCode);
    dispatch(setSubscriptionWallChecked(true));

    // Use centralized role navigation system for other org types
    const navigationPath = RoleConstants.getNavigationPath(role);
    history.push(navigationPath);
  };

  const handlePostAuthFlow = async (authResponse: AuthResponse, options: PostAuthFlowOptions = {}): Promise<void> => {
    const { shouldRegisterFCM = true, customNavigation } = options;

    logger.log("🎉 POST AUTH FLOW STARTED!", authResponse);
    logger.log("🎉 POST AUTH FLOW STARTED!", authResponse);
    logger.log("gjfkkrjejrjr [LOGIN] User countryId from response:", authResponse.user?.countryId);
    logger.log("gjfkkrjejrjr [LOGIN] User organization countryId:", authResponse.user?.organization?.countryId);
    logger.log("gjfkkrjejrjr [LOGIN] User countryId from response:", authResponse.user?.countryId);
    logger.log("gjfkkrjejrjr [LOGIN] User organization countryId:", authResponse.user?.organization?.countryId);

    try {
      // 1. Save user and tokens
      localService.saveUser({
        ...authResponse.user,
        access_token: authResponse.access_token,
        refresh_token: authResponse.refresh_token,
      });

      // 1b. Identify user in PostHog for Feature Flags & A/B Testing targeting
      identifyUser(authResponse.user);
      logger.log("Saved access token:", !!localStorage.getItem("access_token"));
      logger.log("gjfkkrjejrjr [LOGIN] User data saved to localStorage:", {
        countryId: authResponse.user?.countryId,
        organizationCountryId: authResponse.user?.organization?.countryId,
        fullUser: authResponse.user,
      });
      logger.log("gjfkkrjejrjr [LOGIN] User data saved to localStorage:", {
        countryId: authResponse.user?.countryId,
        organizationCountryId: authResponse.user?.organization?.countryId,
        fullUser: authResponse.user,
      });

      // 2. Set currency and companyId if available
      if (authResponse.user.organization && authResponse.user.organization.config) {
        localService.setCurrency(authResponse.user.organization.config.currency);
        localService.setCompanyId(authResponse.user.organization.id);
      }

      const organizationId = authResponse.user?.organizationId;
      let activeRecurringSubscription: any = null;

      const loadSubscriptions = async (): Promise<void> => {
        if (organizationId) {
          try {
            logger.log("📦 Fetching subscriptions for all org types, organizationId:", organizationId);
            logger.log("📦 Fetching subscriptions for all org types, organizationId:", organizationId);
            const result = await checkOrganizationSubscriptions(organizationId);
            activeRecurringSubscription = result.activeRecurringSubscription; // Store active recurring subscription for feature flags
            dispatch(setSubscriptionWallChecked(true));
          } catch (error) {
            logger.error("Error fetching subscriptions in postAuthFlow:", error);
            logger.error("Error fetching subscriptions in postAuthFlow:", error);
            dispatch(setSubscriptionWallChecked(true));
            activeRecurringSubscription = null;
          }
        } else {
          logger.log("No organizationId found, skipping subscription fetch");
          dispatch(setSubscriptionWallChecked(true));
          activeRecurringSubscription = null;
        }
      };

      const loadPaymentSetups = async (): Promise<void> => {
        try {
          logger.log("💳 Fetching payment setups in postAuthFlow");
          logger.log("💳 Fetching payment setups in postAuthFlow");
          await dispatch(fetchPaymentSetups());
        } catch (error) {
          logger.error("Error fetching payment setups in postAuthFlow:", error);
          logger.error("Error fetching payment setups in postAuthFlow:", error);
        }
      };

      // Fetch subscriptions first, then use active recurring subscription for feature flags
      await loadSubscriptions();

      await Promise.all([
        loadCurrencies(),
        loadCountries(),
        loadServiceCategories(),
        loadSubscriptionFeatureFlags(activeRecurringSubscription), // Pass active recurring subscription
        loadPaymentSetups(), // Fetch payment setups
      ]);

      // 4. Register FCM token if requested
      if (shouldRegisterFCM) {
        await registerFCMToken();
      }

      // 5. Set up token refresh
      setupTokenRefresh();

      // 6. Set up session monitoring for automatic logout
      localService.setupSessionMonitoring(30000); // Check every 30 seconds

      // 7. Navigate based on role
      const role = authResponse.user.organizationRole?.code?.toUpperCase() || "";
      if (customNavigation) {
        // Even when a custom redirect was requested (e.g. ?redirect=/space/123 from the public
        // space-detail page), we must still enforce org-type gates so that:
        //   • Hosts are NOT forwarded into the customer booking flow
        //   • Corporate customers without an active subscription still hit /subscriber/pricing
        const orgTypeCode = authResponse.user?.organization?.type?.code;
        const orgRoleCode = authResponse.user?.organizationRole?.code;

        const isHostOrgType = [
          "HOST_PROPERTY_MANAGEMENT",
          "HOST_PROPERTY_DISTRIBUTION",
          "PROPERTY_MANAGEMENT_COMPANY",
        ].includes(orgTypeCode);

        const isCustomerOrgType = [
          "CORPORATE_CUSTOMER",
          "INDIVIDUAL_SUBSCRIBER",
          "INDIVIDUAL_CUSTOMER",
          "SUBSCRIBER_CUSTOMER",
          "HOST_CORPORATE_CUSTOMER",
        ].includes(orgTypeCode);

        if (isHostOrgType) {
          // Hosts should never land in the customer space-booking flow.
          // Re-use the full defaultNavigation logic (handles payment-setup redirect too).
          await defaultNavigation(role, authResponse.user);
        } else if (orgTypeCode === "CORPORATE_CUSTOMER" && orgRoleCode !== "ROLE_HOST_CUSTOMER_INDIVIDUAL") {
          // Corporate customers require an active subscription before they can book.
          const organizationId = authResponse.user?.organizationId;
          const subResult = organizationId
            ? await checkOrganizationSubscriptions(organizationId)
            : { hasActive: false };

          dispatch(setSubscriptionWallChecked(true));

          if (subResult.hasActive) {
            // Subscription is valid — honour the custom destination.
            customNavigation(role);
          } else {
            // No subscription — redirect to pricing wall instead.
            logger.log("customNavigation: CORPORATE_CUSTOMER has no active subscription, redirecting to /subscriber/pricing");
            history.push("/subscriber/pricing");
          }
        } else if (isCustomerOrgType) {
          // INDIVIDUAL_SUBSCRIBER, INDIVIDUAL_CUSTOMER, etc. do not require a subscription check.
          customNavigation(role);
        } else {
          // Unknown/unsupported org type — fall back to the default role navigation.
          await defaultNavigation(role, authResponse.user);
        }
      } else {
        await defaultNavigation(role, authResponse.user);
      }

    } catch (error) {
      logger.error("Error in post-auth flow:", error);
      throw error;
    }
  };

  return {
    handlePostAuthFlow,
    registerFCMToken,
    getFCMToken,
    setupTokenRefresh,
    checkOrganizationSubscriptions,
    loadSubscriptionFeatureFlags,
  };
};

/**
 * Standalone function for FCM token registration (useful for components that don't need navigation)
 */
export const registerFCMTokenStandalone = async (): Promise<void> => {
  try {
    const app = getApp();
    const messaging = getMessaging(app);

    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      });

      if (token) {
        await messagingService.registerFCMToken({
          deviceId: await getDeviceId(),
          platform: "web",
          token: token,
        });
        logger.log("FCM token registered successfully");
      }
    }
  } catch (error) {}
};
